import {
  Button,
  DialogActionTrigger,
  ButtonGroup,
  DialogTitle,
  Input,
  Text,
  VStack,
  Flex,
} from "@chakra-ui/react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaPlus } from "react-icons/fa";
import { useState } from "react";
import {
  TableRowsService,
  type TableRowCreate,
  UploadService,
  type ApiError,
} from "../../client";
import { handleError } from "../../utils";
import useCustomToast from "../../hooks/useCustomToast";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";

// Import custom file upload components
import {
  FileUploadDropzone,
  FileUploadList,
  FileUploadRoot,
} from "@/components/ui/file-upload";

import { ProgressBar } from "@/components/ui/progress-bar";

interface TableField {
  field_id: string;
  field_name: string;
  field_type: string;
  is_required: boolean;
  field_code?: string;
  allowed_extension?: string;
  datetime_type?: string;
  options?: Array<{ label: string; color: string }>;
}

interface AddTableRowProps {
  tableId: string;
  disabled?: boolean;
}

const AddTableRow = ({ tableId, disabled = false }: AddTableRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error"
  >("idle");
  const [_file, setFile] = useState<File | null>(null);

  const queryClient = useQueryClient();
  const { showSuccessToast } = useCustomToast();

  const {
    register,
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isValid, isSubmitting },
  } = useForm<{ [key: string]: unknown }>({
    mode: "onChange",
    criteriaMode: "all",
  });

  // ✅ 필드 메타데이터 조회
  const { data, isLoading: isMetaLoading } = useQuery({
    queryKey: ["openSearchDocs", tableId],
    queryFn: () =>
      TableRowsService.readTableRows({ tableId, skip: 0, limit: 1 }),
    enabled: !disabled && !!tableId,
  });

  const mutation = useMutation({
    mutationFn: (data: TableRowCreate) =>
      TableRowsService.createTableRow({ tableId, requestBody: data }),
    onSuccess: () => {
      showSuccessToast("Row added successfully.");
      reset();
      setFile(null); // clear local file state
      setUploadProgress(0); // reset progress bar
      setUploadStatus("idle");
      setIsOpen(false);
    },
    onError: (err) => handleError(err as ApiError),
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "openSearchDocs" &&
          typeof query.queryKey[1] === "object" &&
          (query.queryKey[1] as { tableId: string }).tableId === tableId,
      });
    },
  });

  const onSubmit = (data: Record<string, unknown>) => {
    mutation.mutate({ data });
  };

  function getContrastColor(bgColor: string) {
    // Simple luminance-based contrast check
    const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
    const r = Number.parseInt(color.substring(0, 2), 16);
    const g = Number.parseInt(color.substring(2, 4), 16);
    const b = Number.parseInt(color.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.6 ? "#000000" : "#FFFFFF";
  }

  const renderInputByType = (field: TableField) => {
    const { field_type, field_name, datetime_type } = field;

    switch (field_type) {
      case "number":
        return (
          <Input
            placeholder={field_name}
            type="number"
            {...register(field_name, {
              required: field.is_required ? `${field_name} is required` : false,
              valueAsNumber: true,
            })}
          />
        );

      case "string":
        return (
          <Input
            placeholder={field_name}
            type="text"
            {...register(field_name, {
              required: field.is_required ? `${field_name} is required` : false,
            })}
          />
        );

      case "datetime": {
        const dtType = datetime_type || "datetime";

        return (
          <Controller
            control={control}
            name={field_name}
            rules={{
              required: field.is_required ? `${field_name} is required` : false,
            }}
            render={({ field: { onChange, value, ref } }) => {
              // 안전한 날짜 변환 함수
              const getSafeDate = (dateValue: unknown): Date | null => {
                if (!dateValue) return null;

                try {
                  const date = new Date(dateValue as string);
                  // 유효한 날짜인지 확인
                  return Number.isNaN(date.getTime()) ? null : date;
                } catch (error) {
                  console.warn(
                    `Invalid date value for ${field_name}:`,
                    dateValue
                  );
                  return null;
                }
              };

              return (
                <ReactDatePicker
                  selected={getSafeDate(value)}
                  onChange={(date: Date | null) => {
                    if (!date) {
                      onChange(null);
                      return;
                    }

                    let formatted: string;
                    if (dtType === "date") {
                      formatted = format(date, "yyyy-MM-dd");
                    } else if (dtType === "time") {
                      formatted = `1970-01-01T${format(date, "HH:mm:ss")}`;
                    } else {
                      const utcDate = fromZonedTime(date, "Asia/Seoul");
                      formatted = format(utcDate, "yyyy-MM-dd'T'HH:mm:ss");
                    }

                    onChange(formatted);
                  }}
                  showTimeSelect={dtType !== "date"}
                  showTimeSelectOnly={dtType === "time"}
                  dateFormat={
                    dtType === "date"
                      ? "yyyy/MM/dd"
                      : dtType === "time"
                        ? "HH:mm"
                        : "yyyy/MM/dd HH:mm"
                  }
                  placeholderText={
                    dtType === "date"
                      ? "YYYY/MM/DD"
                      : dtType === "time"
                        ? "HH:mm"
                        : "YYYY/MM/DD HH:mm"
                  }
                  customInput={
                    <Input
                      ref={ref}
                      placeholder={field_name}
                      borderColor="gray.300"
                      border="1px solid"
                    />
                  }
                />
              );
            }}
          />
        );
      }

      case "selection":
        return (
          <Controller
            control={control}
            name={field_name}
            rules={{
              required: field.is_required ? `${field_name} is required` : false,
            }}
            render={({ field: { onChange, value = "" } }) => (
              <Flex wrap="wrap" gap={2}>
                {(field.options || []).map(
                  (opt: { label: string; color: string }, i: number) => {
                    const isSelected = value === opt.label;
                    const textColor = getContrastColor(opt.color);

                    return (
                      <Button
                        key={`${opt.label}-${i}`}
                        onClick={() => onChange(opt.label)}
                        size="sm"
                        variant="outline"
                        bg={opt.color}
                        color={textColor}
                        border={
                          isSelected
                            ? "3px solid #2B6CB0"
                            : "2px solid transparent"
                        }
                        boxShadow={isSelected ? "0 0 0 2px #90CDF4" : "none"}
                        fontWeight={isSelected ? "bold" : "normal"}
                        opacity={isSelected ? 1 : 0.8}
                        _hover={{
                          opacity: 0.95,
                          transform: "scale(1.03)",
                        }}
                        _active={{ transform: "scale(0.97)" }}
                      >
                        {opt.label}
                      </Button>
                    );
                  }
                )}
              </Flex>
            )}
          />
        );

      case "file":
        return (
          <Controller
            control={control}
            name={field_name}
            rules={{
              required: field.is_required ? `${field_name} is required` : false,
            }}
            render={({ field: { onChange } }) => (
              <Field>
                <FileUploadRoot
                  maxW="xl"
                  alignItems="stretch"
                  maxFiles={1}
                  inputProps={{
                    accept: field.allowed_extension
                      ? `.${field.allowed_extension}`
                      : undefined,
                    onChange: async (e) => {
                      if (isUploading) return;
                      setIsUploading(true);
                      setUploadProgress(0);
                      setUploadStatus("uploading");

                      try {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;

                        const file = files[0];
                        const fileMetadata = {
                          file_name: file.name,
                          file_extension:
                            file?.name?.split(".").pop()?.toLowerCase() ?? "",
                          file_size: file.size,
                          file_type: file.type,
                        };

                        const { url, object_id, bucket } =
                          await UploadService.getPresignedUrl({
                            tableId: tableId,
                            contentType: file.type,
                          });

                        const xhr = new XMLHttpRequest();
                        xhr.upload.onprogress = (event) => {
                          if (event.lengthComputable) {
                            const percent = Math.round(
                              (event.loaded / event.total) * 100
                            );
                            setUploadProgress(percent);
                          }
                        };

                        xhr.onload = () => {
                          if (xhr.status >= 200 && xhr.status < 300) {
                            setFile(file);
                            setUploadStatus("success");
                            onChange({
                              file_metadata: {
                                object_id,
                                bucket,
                                ...fileMetadata,
                              },
                            });
                            void trigger(field_name);
                            e.target.value = ""; // allow re-select
                          } else {
                            setUploadStatus("error");
                            console.error("Upload failed:", xhr.responseText);
                            alert("파일 업로드에 실패했습니다.");
                          }
                        };

                        xhr.onerror = () => {
                          setUploadStatus("error");
                          alert("파일 업로드 중 오류 발생");
                        };

                        xhr.open("PUT", url, true);
                        xhr.setRequestHeader("Content-Type", file.type);
                        xhr.send(file);
                      } catch (error) {
                        setUploadStatus("error");
                        console.error("❌ Upload to MinIO failed:", error);
                        alert("파일 업로드에 실패했습니다.");
                      } finally {
                        setIsUploading(false);
                      }
                    },
                  }}
                >
                  <FileUploadDropzone
                    label="파일을 드래그하거나 클릭해서 업로드하세요"
                    description={`지원되는 형식: ${field.allowed_extension?.toUpperCase() || "모든 파일"}`}
                  />
                  <FileUploadList showSize={true} />

                  {uploadStatus === "uploading" && (
                    <ProgressBar value={uploadProgress} label="업로드 중..." />
                  )}
                  {uploadStatus === "success" && (
                    <ProgressBar
                      value={uploadProgress}
                      label="✅ 업로드 완료"
                    />
                  )}
                  {uploadStatus === "error" && (
                    <Text fontSize="sm" color="red.500" mt={2}>
                      ❌ 업로드 실패
                    </Text>
                  )}
                </FileUploadRoot>
              </Field>
            )}
          />
        );

      default:
        return <Input placeholder="Unsupported field type" disabled />;
    }
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
    >
      <DialogTrigger asChild>
        <Button my={4} disabled={disabled}>
          <FaPlus fontSize="16px" />
          데이터 추가
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>데이터 추가</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>이 테이블에 정의된 항목을 입력하세요.</Text>
            <VStack gap={4}>
              {!isMetaLoading &&
                (data?.fields as TableField[] | undefined)?.map((field) => (
                  <Field
                    key={field.field_id}
                    required={field.is_required}
                    invalid={!!errors[field.field_name]}
                    errorText={
                      errors[field.field_name]?.message?.toString() ?? ""
                    }
                    label={field.field_name}
                  >
                    {renderInputByType(field)}
                  </Field>
                ))}
            </VStack>
          </DialogBody>
          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  취소
                </Button>
              </DialogActionTrigger>
              <Button
                type="submit"
                variant="solid"
                disabled={!isValid}
                loading={isSubmitting}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default AddTableRow;
